import { createRouter, createWebHistory, type RouteLocationNormalized } from 'vue-router'
import { isAuthenticated } from '@/services/authService';
import { useUserPreferenceStore } from '@/stores';
import HomeView from '../views/HomeView.vue'
import GenericRouterView from '@/views/GenericRouterView.vue';

import setupRoutes from '@/views/setup/routes';

// Allows typing the meta properties for routes
declare module 'vue-router' {
  interface RouteMeta {
    authRequired?: boolean,
    showInNav?: boolean;
    navText?: string;
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: { authRequired: true },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/auth/SignIn.vue'),
    },
    {
      path: '/forgot-password',
      name: 'forgotPassword',
      component: () => import('@/views/auth/ForgotPassword.vue'),
    },
    {
      path: '/reset-password',
      name: 'resetPassword',
      component: () => import('@/views/auth/ResetPassword.vue'),
    },
    {
      path: '/signup',
      name: 'signup',
      component: () => import('@/views/auth/SignUp.vue'),
    },
    {
      path: '/signup/verify',
      name: 'signup.verify',
      component: () => import('@/views/auth/VerifyEmail.vue'),
    },
    {
      path: '/signup/confirm',
      name: 'signup.confirm',
      component: () => import('@/views/auth/ConfirmAccount.vue'),
    },
    {
      path: '/user',
      name: 'user',
      component: GenericRouterView,
      children: [
        {
          path: 'profile',
          name: 'user.edit.profile',
          component: () => import('@/views/user/EditProfileMain.vue'),
          meta: { title: 'Edit My Profile', authRequired: true, }
        },
      ],
    },
    {
      path: '/collections',
      name: 'collections',
      component: GenericRouterView,
      children: [
        {
          path: '',
          name: 'collections.list',
          component: () => import('@/views/collections/CollectionList.vue'),
          meta: { title: 'Collections', authRequired: true },
        },
        {
          path: ':id',
          name: 'collections.details',
          component: () => import('@/views/collections/CollectionDetails.vue'),
          meta: { title: 'Collections', authRequired: true, },
          props: route => ({ id: Number(route.params.id) }),
        },
      ],
    },
    {
      path: '/sync-logs',
      name: 'syncLogs',
      component: GenericRouterView,
      children: [
        {
          path: '',
          name: 'syncLogs.list',
          component: () => import('@/views/sync/LogList.vue'),
          meta: { title: 'Sync Logs', authRequired: true },
          props: route => ({ collectionId: Number(route.query.collectionId )}),
        },
        {
          path: ':id',
          name: 'syncLogs.details',
          component: () => import('@/views/sync/LogDetails.vue'),
          meta: { title: 'Sync Log Details', authRequired: true, },
          props: route => ({ id: Number(route.params.id) }),
        },
      ],
    },
    {
      path: '/locomotives',
      name: 'locomotives',
      component: GenericRouterView,
      children: [
        {
          path: '',
          name: 'locomotives.list',
          component: () => import('@/views/locomotives/LocomotiveList.vue'),
          meta: { title: 'Locomotives', authRequired: true, }
        },
        {
          path: ':id',
          name: 'locomotives.details',
          component: () => import('@/views/locomotives/LocomotiveDetails.vue'),
          meta: { title: 'Locomotives', authRequired: true, },
          props: route => ({ id: Number(route.params.id) }),
        },
        {
          path: 'new',
          name: 'locomotives.add',
          component: () => import('@/views/locomotives/AddLocomotive.vue'),
          meta: { title: 'Add Locomotive', authRequired: true, }
        },
        {
          path: 'edit/:id',
          name: 'locomotives.edit',
          component: () => import('@/views/locomotives/EditLocomotive.vue'),
          meta: { title: 'Edit Locomotive', authRequired: true, },
          props: route => ({ id: Number(route.params.id) }),
        },
        {
          path: 'import',
          name: 'locomotives.import',
          component: () => import('@/views/locomotives/ImportLocomotives.vue'),
          meta: { title: 'Import Locomotives', authRequired: true, },
        },
      ]
    },
    {
      path: '/cars',
      name: 'cars',
      component: GenericRouterView,
      children: [
        {
          path: '',
          name: 'cars.list',
          component: () => import('@/views/cars/CarList.vue'),
          meta: { title: 'Cars', authRequired: true, }
        },
        {
          path: ':id',
          name: 'cars.details',
          component: () => import('@/views/cars/CarDetails.vue'),
          meta: { title: 'Cars', authRequired: true, },
          props: route => ({ id: Number(route.params.id) }),
        },
        {
          path: 'new',
          name: 'cars.add',
          component: () => import('@/views/cars/AddCar.vue'),
          meta: { title: 'Add Car', authRequired: true, }
        },
        {
          path: 'edit/:id',
          name: 'cars.edit',
          component: () => import('@/views/cars/EditCar.vue'),
          meta: { title: 'Edit Car', authRequired: true, },
          props: route => ({ id: Number(route.params.id) }),
        },
        {
          path: 'import',
          name: 'cars.import',
          component: () => import('@/views/cars/ImportCars.vue'),
          meta: { title: 'Import Cars', authRequired: true, },
        },
      ]
    },
    {
      path: '/share',
      name: 'share',
      meta: { authRequired: true },
      component: GenericRouterView,
      children: [
        {
          path: '',
          name: 'share.list',
          component: () => import('@/views/sharing/ShareLinkList.vue'),
        },
        {
          path: 'view/:slug',
          name: 'share.publicView',
          meta: { authRequired: false },
          component: () => import('@/views/sharing/PublicShareView.vue'),
        },
      ],
    },
    {
      path: '/car-cards',
      name: 'car-cards',
      meta: { authRequired: true },
      component: GenericRouterView,
      children: [
        {
          path: 'configs',
          name: 'car-cards.configs',
          component: () => import('@/views/carCards/ConfigsList.vue'),
        },
        {
          path: 'configs/new',
          name: 'car-cards.configs.new',
          component: () => import('@/views/carCards/ConfigEditor.vue'),
        },
        {
          path: 'configs/edit/:id',
          name: 'car-cards.configs.edit',
          component: () => import('@/views/carCards/ConfigEditor.vue'),
          props: (route) => ({ id: Number(route.params.id) } ),
        },
      ],
    },
    ...setupRoutes,
  ],
  scrollBehavior: (to) => {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      }
    }

    // always scroll to top
    return { top: 0 };
  },
})

const handleAuthRequired = async (to: RouteLocationNormalized) => {
  const isAuthd = await isAuthenticated();

  if (to.meta.authRequired && to.name !== 'login' && !isAuthd) {
    return `/login?redirectTo=${to.fullPath}`;
  }
};

const handleLoadPreferences = async(to: RouteLocationNormalized) => {
  const isAuthd = await isAuthenticated();

  if (to.meta.authRequired && isAuthd) {
    // console.log('load preferences');
    const userPreferenceStore = useUserPreferenceStore();
    await userPreferenceStore.loadPreferences();
  }
}

router.beforeEach(handleAuthRequired);
router.beforeEach(handleLoadPreferences);

export default router
